import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  ChangeDetectorRef, OnDestroy
} from '@angular/core';
import {Router} from "@angular/router";
import {DraftBody} from "../../../../../model/DraftBody";
import {StartupService} from "../../../../service-ui/startup.service";
import {Draft} from "../../../../../model/Draft";
import {FormControl, FormGroup} from "@angular/forms";
import {DraftService} from "../../../../service-data/draft.service";
import {DraftBodyService} from "../../../../service-data/draft-body.service";
import {Subject} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {MatDialog} from "@angular/material/dialog";
import {LinkSidesWarningDialogComponent} from "./link-sides-warning-dialog.component";
import {TRIM_SIZE_PRESETS, TrimSizePreset} from "../project-draft.component";
import {ToolbarPreferencesService} from "../../../../service-ui/toolbar-preferences.service";
import {MediaMatcher} from "@angular/cdk/layout";
import {LanguagePipe} from "../../../../pipe/language.pipe";
import {MeasurementUnits} from "../../../../service-ui/measurement-units";


@Component({
  selector: 'app-project-draft-body',
  templateUrl: './project-draft-body.component.html',
  styleUrls: ['./project-draft-body.component.css']
})
export class ProjectDraftBodyComponent implements OnInit, AfterViewInit, OnDestroy {
  // @ViewChild('group') buttonToggleGroup!: MatButtonToggleGroup;
  // @ViewChild('group', { static: false, read: ElementRef }) buttonToggleGroup!: ElementRef;
  // @ViewChild('buttonToggleContainer') container!: ElementRef;
  @Input() draft: Draft;
  @Input() draftBody: DraftBody;
  @ViewChild('contentArea', { static: false }) contentAreaRef!: ElementRef<HTMLDivElement>;

  sliderTick = 10; // Tick mark interval
  sliderStep = 2; // Thumb width
  sliderMin = 4;

  unit: 'in' | 'mm' = 'in';
  unitMultiplier = 72;

  showMarginCheck: boolean = true; // Will be loaded from preferences
  showRulers: boolean = false; // Will be loaded from preferences
  // showMoreOptions removed — margins now in toolbarExpanded section
  showDebugGrid: boolean = false; // Will be loaded from preferences
  showGutter: boolean = true; // Will be loaded from preferences
  showPageNumbers: boolean = false; // Will be loaded from preferences

  pageSize = 10;
  pageRanges: { start: number, end: number }[] = [];
  selectedPageRange: { start: number, end: number } = { start: 0, end: 9 };
  showScrollButtons = false;
  pageRangeSliderForm = new FormGroup({
    pageRangeSlider: new FormControl(0) // Initialize with a default value
  });

  // Scale/zoom value: 0.25 = 100% display (with 400x multiplier), matching production baseline
  // Shows 288 DPI spreads at natural pixel size at 0.25
  // Initially calculated to fit spread width, but user can adjust
  scaleValue: number = 0.25;

  controlPositions = ['bottom-left', 'center', 'top-left', 'top-right', 'bottom-right'];
  currentControlPosition: string = 'bottom-left';
  interiorDragPosition: { x: number, y: number } = { x: 100, y: 400 };

  linkSides: boolean = false;
  toolbarExpanded: boolean = false; // Controls whether to show trim size and units on mobile

  Math = Math; // For template access

  trimSizePresets = TRIM_SIZE_PRESETS;
  selectedTrimSize: string = '';
  customWidth: number = 0;
  customHeight: number = 0;

  // Custom trim edit mode
  editingCustomTrim: boolean = false;
  editCustomWidth: number = 0;
  editCustomHeight: number = 0;

  private saveSubject = new Subject<void>();
  private onDestroySubject = new Subject<void>();

  constructor (public router: Router, public startupService: StartupService, private renderer: Renderer2, private cdr: ChangeDetectorRef, private draftService: DraftService, private draftBodyService: DraftBodyService, private dialog: MatDialog, private toolbarPrefs: ToolbarPreferencesService, private media: MediaMatcher) {
  }

  get isRtl(): boolean {
    return this.startupService.dir === 'rtl';
  }

  /** Transform digits to locale-specific characters (e.g., Arabic-Indic numerals) */
  localizeNumber(value: any): string {
    return LanguagePipe.localizeNumber(value);
  }

  /** Parse locale-specific digits back to Western Arabic for numeric processing */
  delocalizeNumber(value: string): number {
    return LanguagePipe.delocalizeNumber(value);
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);

    // Determine default expanded state based on screen width
    const isNarrowScreen = this.media.matchMedia('(max-width: 900px)').matches;
    const defaultExpanded = !isNarrowScreen;

    // Load saved preferences or use defaults
    this.toolbarExpanded = this.toolbarPrefs.getInteriorToolbarExpanded(defaultExpanded);
    this.currentControlPosition = this.toolbarPrefs.getInteriorToolbarPosition('bottom-left');
    this.interiorDragPosition = this.toolbarPrefs.getInteriorToolbarXY(null);
    if (!this.interiorDragPosition) {
      // Default: 10% from left, 60% from top of viewport
      this.interiorDragPosition = { x: Math.round(window.innerWidth * 0.1), y: Math.round(window.innerHeight * 0.6) };
    }

    // Load display toggle preferences with correct defaults:
    // - Margin lines: ON by default
    // - Spine gutter: ON by default
    // - Rulers: OFF by default
    // - Page numbers: OFF by default
    // - Grid lines: OFF by default
    this.showMarginCheck = this.toolbarPrefs.getShowMarginCheck(true);
    this.showGutter = this.toolbarPrefs.getShowGutter(true);
    this.showRulers = this.toolbarPrefs.getShowRulers(false);
    this.showPageNumbers = this.toolbarPrefs.getShowPageNumbers(false);
    this.showDebugGrid = this.toolbarPrefs.getShowDebugGrid(false);

    // Debounce saves to avoid excessive API calls
    this.saveSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.draftBodyService.updateItem(this.draftBody).subscribe();
    });
    this.initTrimSizeSelection();
    this.initLinkSides();
  }

  /** Auto-detect linkSides from shift values based on current DraftBody state */
  private initLinkSides(): void {
    if (this.draftBody) {
      // Auto-detect: link if vertical shifts are same and horizontal shifts are negatives of each other
      const vSame = this.draftBody.leftVerticalShift === this.draftBody.rightVerticalShift;
      const hMirrored = this.draftBody.leftHorizontalShift === -this.draftBody.rightHorizontalShift;
      this.linkSides = vSame && hMirrored;
    }
  }

  /** Determine which preset matches the current draft body dimensions, or set to Custom */
  initTrimSizeSelection() {
    if (!this.draftBody) { return; }
    const widthIn = this.draftBody.width / 72;
    const heightIn = this.draftBody.height / 72;
    const match = this.trimSizePresets.find(p =>
      Math.abs(p.width - widthIn) < 0.01 && Math.abs(p.height - heightIn) < 0.01
    );
    if (match) {
      this.selectedTrimSize = match.label;
    } else {
      this.selectedTrimSize = 'CUSTOM';
      this.customWidth = parseFloat(widthIn.toFixed(4));
      this.customHeight = parseFloat(heightIn.toFixed(4));
    }
  }

  /** Called when the user picks a preset or "Custom" from the combo box */
  onTrimSizePresetChange(value: string) {
    this.selectedTrimSize = value;
    if (value === 'CUSTOM') {
      this.customWidth = parseFloat((this.draftBody.width / 72).toFixed(4));
      this.customHeight = parseFloat((this.draftBody.height / 72).toFixed(4));
      this.editCustomWidth = this.customWidth;
      this.editCustomHeight = this.customHeight;
      this.editingCustomTrim = true;
    } else {
      const preset = this.trimSizePresets.find(p => p.label === value);
      if (preset) {
        this.draftBody.width = preset.width * 72;
        this.draftBody.height = preset.height * 72;
        this.customWidth = preset.width;
        this.customHeight = preset.height;
        this.editingCustomTrim = false;
        this.saveSubject.next();
      }
    }
  }

  /** True when the edit values differ from the saved custom values */
  get customTrimChanged(): boolean {
    return this.editCustomWidth !== this.customWidth || this.editCustomHeight !== this.customHeight;
  }

  /** Save the custom trim values and exit edit mode */
  saveCustomTrim(): void {
    if (this.editCustomWidth > 0 && this.editCustomHeight > 0) {
      this.customWidth = this.editCustomWidth;
      this.customHeight = this.editCustomHeight;
      this.draftBody.width = this.customWidth * 72;
      this.draftBody.height = this.customHeight * 72;
      this.editingCustomTrim = false;
      this.saveSubject.next();
    }
  }

  /** Cancel custom trim editing and revert to saved values */
  cancelCustomTrim(): void {
    this.editingCustomTrim = false;
    this.initTrimSizeSelection();
  }

  /**
   * The translated label for this editor's currently selected ruler
   * unit. Resolved through `MeasurementUnits` so `in` / `mm` is a real
   * translation row rather than a literal baked into the template --
   * see `docs/i18n/untranslatable.md`.
   */
  getUnitLabel(): string {
    return MeasurementUnits.lengthLabelFor(this.unit);
  }

  /** Get the display label for a trim size preset based on current units */
  getTrimSizeLabel(preset: any): string {
    if (this.unit === 'mm') {
      const widthMm = Math.round(preset.width * 25.4);
      const heightMm = Math.round(preset.height * 25.4);
      return `${this.localizeNumber(widthMm)} × ${this.localizeNumber(heightMm)} ${this.getUnitLabel()}`;
    } else {
      return `${this.localizeNumber(preset.width)} × ${this.localizeNumber(preset.height)} ${this.getUnitLabel()}`;
    }
  }

  /**
   * "8.5 × 11 in" for the committed custom trim size.
   *
   * Reads `customWidth`/`customHeight` (the applied values), NOT
   * `editCustomWidth`/`editCustomHeight` (the in-progress edit
   * buffer) -- the closed `mat-option` must show what is actually in
   * effect.
   */
  getCustomTrimSizeLabel(): string {
    return this.getTrimSizeLabel({ width: this.customWidth, height: this.customHeight });
  }

  /** Get custom width in current units for display */
  getCustomWidthInCurrentUnits(): number {
    if (this.unit === 'mm') {
      return Math.round(this.editCustomWidth * 25.4);
    } else {
      return parseFloat(this.editCustomWidth.toFixed(4));
    }
  }

  /** Set custom width from value in current units */
  setCustomWidthFromCurrentUnits(value: number): void {
    if (this.unit === 'mm') {
      this.editCustomWidth = value / 25.4;
    } else {
      this.editCustomWidth = value;
    }
  }

  /** Get custom height in current units for display */
  getCustomHeightInCurrentUnits(): number {
    if (this.unit === 'mm') {
      return Math.round(this.editCustomHeight * 25.4);
    } else {
      return parseFloat(this.editCustomHeight.toFixed(4));
    }
  }

  /** Set custom height from value in current units */
  setCustomHeightFromCurrentUnits(value: number): void {
    if (this.unit === 'mm') {
      this.editCustomHeight = value / 25.4;
    } else {
      this.editCustomHeight = value;
    }
  }

  ngAfterViewInit(): void {
    // Calculate initial zoom to fit spread without horizontal scrolling
    this.calculateInitialZoom();

    // If toolbar was dragged to a custom position, apply it
    if (this.currentControlPosition === 'custom' && this.interiorDragPosition && this.interiorToolbarRef) {
      const el = this.interiorToolbarRef.nativeElement;
      el.style.top = this.interiorDragPosition.y + 'px';
      el.style.left = this.interiorDragPosition.x + 'px';
      el.style.bottom = 'auto';
      el.style.right = 'auto';
      el.style.transform = 'none';
    }
  }

  /** Calculate zoom to fit the entire spread (two pages) in the viewport */
  calculateInitialZoom(): void {
    if (!this.contentAreaRef || !this.draftBody) return;

    const containerWidth = this.contentAreaRef.nativeElement.clientWidth;

    // Calculate spread width at 288 DPI
    const trimWidthInches = this.draftBody.width / 72;
    const pageWidthPx = trimWidthInches * 288; // Single page width at 288 DPI
    const spreadWidthPx = (pageWidthPx + 18) * 2 + 40; // Two pages + borders + padding

    // Calculate zoom to fit
    const zoomToFit = containerWidth / spreadWidthPx;

    // Set initial zoom, but don't go below 0.05 (20%) or above 0.25 (100%) for initial view
    this.scaleValue = Math.max(0.05, Math.min(0.25, zoomToFit));
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
    this.saveSubject.complete();
    if (this.boundDragMove) {
      document.removeEventListener('mousemove', this.boundDragMove);
      document.removeEventListener('touchmove', this.boundDragMove);
    }
    if (this.boundDragEnd) {
      document.removeEventListener('mouseup', this.boundDragEnd);
      document.removeEventListener('touchend', this.boundDragEnd);
    }
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
    }
  }

  // pageRangeSelected(selectedRange: any) {
  //   if ( selectedRange.end - selectedRange.start > 20 ) {
  //     this.selectedPageRange = this.calculatePageRanges(selectedRange.start);
  //   } else {
  //     this.selectedPageRange = selectedRange;
  //   }
  // }

  pageNumbers(start: number, end: number, countBy: number) {
    const pageNumbers = [];
    for ( let i = start; i <= end; i += countBy ) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  }

  formatLabel(value: number) {
    if ( this.draft && this.draft.draftBody ) {
      return `${value} - ${Math.min(value + this.pageSize - 1, this.draft.draftBody.numPages)}`; // Calculate end of range based on thumb width
    } else {
      return `${value} - ${value + this.pageSize - 1}`; // Calculate end of range based on thumb width
    }
  }

  onSliderChange(newValue: number) {
    let value = Number(newValue);
    value = Math.min(value, this.draft.draftBody.numPages - this.pageSize - 1);
    this.selectedPageRange.start = value;
    if ( this.draft && this.draft.draftBody ) {
      this.selectedPageRange = {
        start: value,
        end: value + this.pageSize - 1
      };
    } else {
      this.selectedPageRange = {
        start: value,
        end: value + this.pageSize - 1
      };
    }
  }

  prevPageRange() {
    const newStart = Math.max(0, this.selectedPageRange.start - this.pageSize);
    this.onSliderChange(newStart);
  }

  nextPageRange() {
    const maxStart = Math.max(0, this.draft.draftBody.numPages - this.pageSize);
    const newStart = Math.min(maxStart, this.selectedPageRange.start + this.pageSize);
    this.onSliderChange(newStart);
  }

  get hasPrevPages(): boolean {
    return this.selectedPageRange.start > 0;
  }

  get hasNextPages(): boolean {
    return this.selectedPageRange.end < this.draft.draftBody.numPages - 1;
  }

  get pageRangeLabel(): string {
    const start = this.selectedPageRange.start === 0 ? 1 : this.selectedPageRange.start;
    const end = Math.min(this.selectedPageRange.end, this.draft.draftBody.numPages);
    return `${this.localizeNumber(start)}–${this.localizeNumber(end)} / ${this.localizeNumber(this.draft.draftBody.numPages)}`;
  }

  onPageBarClick(event: MouseEvent) {
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    let fraction = (event.clientX - rect.left) / rect.width;
    if (this.isRtl) {
      fraction = 1 - fraction; // In RTL, right edge is page 0
    }
    const clickedPage = Math.round(fraction * this.draft.draftBody.numPages);
    // Center the page range on the clicked position
    const halfPage = Math.floor(this.pageSize / 2);
    const newStart = Math.max(0, Math.min(clickedPage - halfPage, this.draft.draftBody.numPages - this.pageSize));
    this.onSliderChange(newStart);
  }

  get tickValues() {
    const ticks = [];
    for (let i = 0; i <= this.draft.draftBody.numPages; i += this.sliderTick) {
      ticks.push(i);
    }
    return ticks;
  }

  get rangeIndicatorStyle() {
    const sliderWidth = 100; // Assuming the slider takes up 100% width of its container
    const rangeWidth = ((this.selectedPageRange.end - this.selectedPageRange.start ) / this.draft.draftBody.numPages) * sliderWidth;
    const leftOffset = (this.selectedPageRange.start / this.draft.draftBody.numPages) * sliderWidth;

    return {
      width: `${rangeWidth}%`,
      left: `${leftOffset}%`,
    };
  }

  // calculatePageRanges(currentPage: number): { start: number, end: number } | null {
  //   const numPages = this.draft.draftBody.numPages + 2;
  //   this.pageRanges = []; // Reintroduce the pageRanges array
  //   let rangeToReturn = { start: 1, end: 9 };
  //
  //   for (let i = 0; i < numPages; i += 50) {
  //     const start = i;
  //     const end = Math.min(i + 49, numPages - 1);
  //
  //     if (currentPage >= start && currentPage <= end) {
  //       // If currentPage is within this 50-page range, further subdivide
  //       for (let j = start; j <= end; j += 10) {
  //         const subStart = j;
  //         const subEnd = Math.min(j + 9, end);
  //         this.pageRanges.push({ start: subStart, end: subEnd }); // Add the sub-range
  //         if (currentPage >= subStart && currentPage <= subEnd) {
  //           rangeToReturn = { start: subStart, end: subEnd }; // Return the matching 10-page range
  //         }
  //       }
  //     } else {
  //       // Add the default 50-page range
  //       this.pageRanges.push({ start, end });
  //     }
  //   }
  //   return rangeToReturn;
  // }
  // checkScrollButtons() {
  //   const containerWidth = this.container.nativeElement.offsetWidth;
  //   const groupWidth = this.buttonToggleGroup.nativeElement.offsetWidth; // Access offsetWidth directly
  //   this.showScrollButtons = groupWidth > containerWidth;
  // }
  //
  // scrollLeft() {
  //   this.container.nativeElement.scrollLeft -= 100; // Scroll the container instead
  // }
  //
  // scrollRight() {
  //   this.container.nativeElement.scrollLeft += 100; // Scroll the container instead
  // }
  //


  // Cycle through the positions (legacy, kept for compatibility)
  moveControl(): void {
    const currentIndex = this.controlPositions.indexOf(this.currentControlPosition);
    const nextIndex = (currentIndex + 1) % this.controlPositions.length;
    this.currentControlPosition = this.controlPositions[nextIndex];
    this.toolbarPrefs.setInteriorToolbarPosition(this.currentControlPosition);
  }

  @ViewChild('interiorToolbar', { static: false }) interiorToolbarRef: ElementRef;

  /** Native drag implementation for floating toolbar */
  private dragOffset = { x: 0, y: 0 };
  private boundDragMove: (e: MouseEvent | TouchEvent) => void;
  private boundDragEnd: (e: MouseEvent | TouchEvent) => void;

  onDragStart(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    const el = this.interiorToolbarRef?.nativeElement;
    if (!el) { return; }
    const rect = el.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.dragOffset = { x: clientX - rect.left, y: clientY - rect.top };

    this.boundDragMove = (e: MouseEvent | TouchEvent) => this.onDragMove(e);
    this.boundDragEnd = () => this.onDragEnd();
    document.addEventListener('mousemove', this.boundDragMove);
    document.addEventListener('mouseup', this.boundDragEnd);
    document.addEventListener('touchmove', this.boundDragMove, { passive: false });
    document.addEventListener('touchend', this.boundDragEnd);
  }

  private onDragMove(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    const el = this.interiorToolbarRef?.nativeElement;
    if (!el) { return; }
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    // Clear any CSS class positioning and set absolute top/left
    el.style.top = (clientY - this.dragOffset.y) + 'px';
    el.style.left = (clientX - this.dragOffset.x) + 'px';
    el.style.bottom = 'auto';
    el.style.right = 'auto';
    el.style.transform = 'none';
  }

  private onDragEnd(): void {
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup', this.boundDragEnd);
    document.removeEventListener('touchmove', this.boundDragMove);
    document.removeEventListener('touchend', this.boundDragEnd);
    const el = this.interiorToolbarRef?.nativeElement;
    if (el) {
      this.toolbarPrefs.setInteriorToolbarXY({
        x: parseInt(el.style.left, 10) || 0,
        y: parseInt(el.style.top, 10) || 0
      });
      // Set position to 'custom' so the CSS class doesn't override
      this.currentControlPosition = 'custom';
      this.toolbarPrefs.setInteriorToolbarPosition('custom');
    }
  }

  // Toggle toolbar expansion for mobile
  toggleToolbarExpanded(): void {
    this.toolbarExpanded = !this.toolbarExpanded;
    this.toolbarPrefs.setInteriorToolbarExpanded(this.toolbarExpanded);
  }

  // Get the CSS class for the current position
  get controlPositionClass(): string {
    return `scale-control-${this.currentControlPosition}`;
  }
  onScaleChange(event: Event): void {
    // Get the value from the event target (the <input> element)
    const target = event.target as HTMLInputElement;
    this.scaleValue = parseFloat(target.value);
  }

  changeUnits(newUnits: "in" | "mm") {
    this.unit = newUnits;
    switch ( this.unit) {
      case "in": {
        this.unitMultiplier = 72;
        break;
      }
      case "mm": {
        this.unitMultiplier = 72 / 25.4;
        break;
      }
    }
  }


  changeShiftValue(prop: ShiftProperty, newValue: number): void {
    newValue = newValue * this.unitMultiplier;
    newValue = Math.min(20 * 72, newValue);
    newValue = Math.max(-20 * 72, newValue);
    this.draftBody[prop] = newValue;

    // If sides are linked, synchronize the other side
    if (this.linkSides) {
      if (prop === 'leftVerticalShift') {
        // Vertical shifts work together (same value)
        this.draftBody.rightVerticalShift = newValue;
      } else if (prop === 'rightVerticalShift') {
        // Vertical shifts work together (same value)
        this.draftBody.leftVerticalShift = newValue;
      } else if (prop === 'leftHorizontalShift') {
        // Horizontal shifts mirror (negative values to achieve same visual shift)
        this.draftBody.rightHorizontalShift = -newValue;
      } else if (prop === 'rightHorizontalShift') {
        // Horizontal shifts mirror (negative values to achieve same visual shift)
        this.draftBody.leftHorizontalShift = -newValue;
      }
    }
    this.saveSubject.next();
  }

  changeShift(prop: ShiftProperty, delta: number): void {
    const currentValue = this.draftBody[prop];
    let newValue = currentValue + delta;
    newValue = Math.min(20 * 72, newValue);
    newValue = Math.max(-20 * 72, newValue);
    this.draftBody[prop] = newValue;

    // If sides are linked, synchronize the other side
    if (this.linkSides) {
      if (prop === 'leftVerticalShift') {
        // Vertical shifts work together (add same delta to both)
        this.draftBody.rightVerticalShift = Math.min(20 * 72, Math.max(-20 * 72, this.draftBody.rightVerticalShift + delta));
      } else if (prop === 'rightVerticalShift') {
        // Vertical shifts work together (add same delta to both)
        this.draftBody.leftVerticalShift = Math.min(20 * 72, Math.max(-20 * 72, this.draftBody.leftVerticalShift + delta));
      } else if (prop === 'leftHorizontalShift') {
        // Horizontal shifts mirror (subtract delta from other side to maintain negative relationship)
        this.draftBody.rightHorizontalShift = Math.min(20 * 72, Math.max(-20 * 72, this.draftBody.rightHorizontalShift - delta));
      } else if (prop === 'rightHorizontalShift') {
        // Horizontal shifts mirror (subtract delta from other side to maintain negative relationship)
        this.draftBody.leftHorizontalShift = Math.min(20 * 72, Math.max(-20 * 72, this.draftBody.leftHorizontalShift - delta));
      }
    }
    this.saveSubject.next();
  }

  // --- New Toggle Methods ---

  /** Round a number to the nearest hundredth for display */
  roundHundredths(value: number): number {
    return Math.round(value * 100) / 100;
  }

  toggleMarginCheck(): void {
    this.showMarginCheck = !this.showMarginCheck;
    this.toolbarPrefs.setShowMarginCheck(this.showMarginCheck);
  }

  toggleRulers(): void {
    this.showRulers = !this.showRulers;
    this.toolbarPrefs.setShowRulers(this.showRulers);
  }

  toggleDebugGrid(): void {
    this.showDebugGrid = !this.showDebugGrid;
    this.toolbarPrefs.setShowDebugGrid(this.showDebugGrid);
  }

  toggleGutter(): void {
    this.showGutter = !this.showGutter;
    this.toolbarPrefs.setShowGutter(this.showGutter);
  }

  togglePageNumbers(): void {
    this.showPageNumbers = !this.showPageNumbers;
    this.toolbarPrefs.setShowPageNumbers(this.showPageNumbers);
  }

  // toggleOptions removed — margins now in toolbarExpanded section

  /** Update a margin value (input is in display units, convert to points) */
  changeMargin(property: 'marginTop' | 'marginBottom' | 'marginOut' | 'marginIn', value: number | string): void {
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      this.draftBody[property] = Math.round(num * this.unitMultiplier * 100) / 100;
      this.saveSubject.next();
    }
  }

  /** Called when the user toggles the Link Sides checkbox */
  onLinkSidesChange(checked: boolean): void {
    if (checked) {
      // Check if left and right values differ
      const vDiff = this.draftBody.leftVerticalShift !== this.draftBody.rightVerticalShift;
      const hDiff = this.draftBody.leftHorizontalShift !== -this.draftBody.rightHorizontalShift;
      if (vDiff || hDiff) {
        const dialogRef = this.dialog.open(LinkSidesWarningDialogComponent, {
          width: '400px',
          data: {
            leftVertical: this.roundHundredths(this.draftBody.leftVerticalShift / this.unitMultiplier),
            leftHorizontal: this.roundHundredths(this.draftBody.leftHorizontalShift / this.unitMultiplier),
            rightVertical: this.roundHundredths(this.draftBody.rightVerticalShift / this.unitMultiplier),
            rightHorizontal: this.roundHundredths(this.draftBody.rightHorizontalShift / this.unitMultiplier),
            unitLabel: this.unit
          }
        });
        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            // Apply right-side values to both sides (vertical same, horizontal negated)
            this.draftBody.leftVerticalShift = this.draftBody.rightVerticalShift;
            this.draftBody.leftHorizontalShift = -this.draftBody.rightHorizontalShift;
            this.linkSides = true;
            this.saveSubject.next();
          } else {
            // Cancelled — ensure checkbox resets to unchecked
            this.linkSides = false;
          }
        });
      } else {
        // Values are already synchronized, just enable linking
        this.linkSides = true;
      }
    } else {
      // User unchecked the link - just disable it (don't change shift values)
      this.linkSides = false;
    }
  }
}

type ShiftProperty =
  'leftVerticalShift' |
  'leftHorizontalShift' |
  'rightVerticalShift' |
  'rightHorizontalShift';

